import React, { isValidElement, useState } from "react";
import { Button, Checkbox, Popover, Radio, Space } from "antd";
import type { RadioChangeEvent } from "antd";
import Icon from "@ant-design/icons";
import { isEmpty } from "lodash";
import { CheckboxGroupProps, CheckboxOptionType } from "antd/es/checkbox";
import { CheckboxValueType } from "antd/es/checkbox/Group";
import * as Icons from "assets/icons";
import styles from "./DropdownInput.module.scss";

interface DropdownInputProps {
  options: Array<CheckboxOptionType | string>;
  placeholder?: string;
  width?: number;
  value?: any;
  onChange?: (checkedValue: any) => void;
  mode?: "multiple" | "single";
}

function DropdownInput({ options, placeholder, width, value, onChange, mode = "multiple" }: DropdownInputProps) {
  const [visible, setVisible] = useState(false);

  const handleCheckboxChange: CheckboxGroupProps["onChange"] = (checkedValue) => {
    onChange && onChange(checkedValue);
  };

  const handleRadioChange = (e: RadioChangeEvent) => {
    onChange && onChange(e.target.value);
    setVisible(false);
  };

  const content =
    mode === "multiple" ? (
      <Checkbox.Group
        value={value}
        options={options}
        onChange={handleCheckboxChange}
        style={width ? { width: `${width}px` } : { width: "auto" }}
        className={styles.dropdownCheckbox}
      />
    ) : (
      <Radio.Group
        value={value}
        onChange={handleRadioChange}
        style={width ? { width: `${width}px` } : { width: "auto" }}
        className={`${styles.dropdownCheckbox} ${styles.dropdownRadio}`}
      >
        <Space direction="vertical" size={0} style={{ width: "100%" }}>
          {options.map((opt) => {
            const optValue = typeof opt === "string" ? opt : opt.value;
            const optLabel = typeof opt === "string" ? opt : opt.label;
            return (
              <Radio key={optValue.toString()} value={optValue}>
                {optLabel}
              </Radio>
            );
          })}
        </Space>
      </Radio.Group>
    );

  let displayText: string;
  if (mode === "single") {
    if (value === undefined || value === null || value === "") {
      displayText = placeholder ?? "";
    } else {
      const selectedOption = options.find((opt) => (typeof opt === "string" ? opt : opt.value) === value);
      if (selectedOption) {
        if (typeof selectedOption === "string") {
          displayText = selectedOption;
        } else if (isValidElement(selectedOption.label)) {
          displayText = selectedOption.label.props?.children?.[0] ?? "";
        } else {
          displayText = selectedOption.label as string;
        }
      } else {
        displayText = placeholder ?? "";
      }
    }
  } else {
    if (isEmpty(value) || value!.length === options.length) {
      displayText = placeholder ?? "";
    } else if (typeof options[0] === "string") {
      displayText = value!.join(",");
    } else {
      displayText = (options as CheckboxOptionType[])
        .filter((option) => value!.includes(option.value))
        .map((option) => {
          if (isValidElement(option.label)) {
            return option.label.props?.children?.[0] ?? "";
          }
          return option.label;
        })
        .join(",");
    }
  }

  return (
    <Popover
      content={content}
      overlayClassName={styles.overlay}
      placement="bottomLeft"
      trigger={["click"]}
      visible={visible}
      onVisibleChange={() => setVisible(!visible)}
    >
      <Button className={styles.dropdownInput}>
        <span>{displayText}</span>
        <Icon component={Icons.DropDown} className={styles.icon} />
      </Button>
    </Popover>
  );
}

export default DropdownInput;
